import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Lock, Shield, Mail, KeyRound, UserPlus, Trash2, Phone, Pencil, Check, X, ShieldCheck } from "lucide-react";
import { api, notifyApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Platform Owners management modal.
 *
 * Flow:
 *   1. Closed → click "Platform Owners" button → prompts for 5-digit PIN.
 *   2. PIN correct → backend grants a 30-min unlock window stored on the
 *      caller's user record → list / add / remove platform owners + change PIN.
 *   3. Forgot PIN → enter your registered platform-owner email → backend
 *      mails a 6-digit code to every platform owner → enter code + new PIN.
 */
export default function PlatformOwnersModal({ open, onClose }) {
  // step: 'pin' | 'forgot' | 'reset' | 'manage'
  const [step, setStep] = useState("pin");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [owners, setOwners] = useState([]);
  const [forgotEmail, setForgotEmail] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmNewPin, setConfirmNewPin] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [newOwner, setNewOwner] = useState({
    method: "email", // "email" | "phone"
    email: "",
    name: "",
    password: "",
    country_code: "+91",
    phone: "",
  });
  const [showChangePin, setShowChangePin] = useState(false);
  const [currentPin, setCurrentPin] = useState("");
  // Inline phone-editor state: { ownerId, country_code, phone }
  const [editingPhone, setEditingPhone] = useState(null);
  // Column view toggle: "email" vs "phone" — emphasises the chosen identifier
  // so the platform admin can scan one column at a time.
  const [viewBy, setViewBy] = useState("email");

  // Reset when re-opened.
  useEffect(() => {
    if (open) {
      setStep("pin");
      setPin("");
      setForgotEmail("");
      setRecoveryCode("");
      setNewPin("");
      setConfirmNewPin("");
      setOwners([]);
      setShowAdd(false);
      setShowChangePin(false);
      setNewOwner({ method: "email", email: "", name: "", password: "", country_code: "+91", phone: "" });
      setCurrentPin("");
      setEditingPhone(null);
    }
  }, [open]);

  const loadOwners = async () => {
    try {
      const { data } = await api.get("/platform/owners");
      setOwners(data.owners || []);
    } catch (e) {
      notifyApiError(toast, e);
    }
  };

  const submitPin = async () => {
    if (!/^\d{5}$/.test(pin)) {
      toast.error("PIN must be 5 digits");
      return;
    }
    setBusy(true);
    try {
      await api.post("/platform/owners/verify-pin", { pin });
      setStep("manage");
      await loadOwners();
      toast.success("Unlocked — 30 min");
    } catch (e) {
      notifyApiError(toast, e);
    } finally {
      setBusy(false);
    }
  };

  const sendForgot = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/platform/owners/forgot-pin", {
        email: forgotEmail.trim().toLowerCase(),
      });
      if ((data?.sent_to_count ?? 0) > 0) {
        toast.success(`Recovery code emailed to ${data.sent_to_count} owner(s)`);
      } else {
        toast.success(
          "Recovery code generated. If your email is a platform owner, you'll receive it shortly.",
        );
      }
      setStep("reset");
    } catch (e) {
      notifyApiError(toast, e);
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async () => {
    if (!/^\d{6}$/.test(recoveryCode)) {
      toast.error("Recovery code must be 6 digits");
      return;
    }
    if (!/^\d{5}$/.test(newPin)) {
      toast.error("New PIN must be 5 digits");
      return;
    }
    if (newPin !== confirmNewPin) {
      toast.error("PINs don't match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/platform/owners/reset-pin", {
        recovery_code: recoveryCode,
        new_pin: newPin,
      });
      toast.success("PIN reset — please enter the new PIN");
      setStep("pin");
      setPin("");
      setNewPin("");
      setConfirmNewPin("");
      setRecoveryCode("");
    } catch (e) {
      notifyApiError(toast, e);
    } finally {
      setBusy(false);
    }
  };

  const addOwner = async () => {
    const phoneDigits = (newOwner.phone || "").replace(/\D/g, "");
    if (newOwner.method === "email") {
      if (!newOwner.email || !newOwner.password) {
        toast.error("Email and password are required");
        return;
      }
    } else {
      if (!phoneDigits) {
        toast.error("Phone number is required");
        return;
      }
    }
    setBusy(true);
    try {
      const body = {
        name: newOwner.name,
        ...(newOwner.method === "email"
          ? { email: newOwner.email, password: newOwner.password }
          : {}),
        ...(phoneDigits
          ? { country_code: newOwner.country_code, phone: phoneDigits }
          : {}),
      };
      const { data } = await api.post("/platform/owners", body);
      const label =
        newOwner.method === "email"
          ? newOwner.email
          : `${newOwner.country_code} ${phoneDigits}`;
      toast.success(`${label} added as platform owner`);
      setShowAdd(false);
      setNewOwner({ method: "email", email: "", name: "", password: "", country_code: "+91", phone: "" });
      await loadOwners();
      void data;
    } catch (e) {
      notifyApiError(toast, e);
    } finally {
      setBusy(false);
    }
  };

  const saveOwnerPhone = async () => {
    if (!editingPhone) return;
    setBusy(true);
    try {
      const digits = (editingPhone.phone || "").replace(/\D/g, "");
      await api.patch(`/platform/owners/${editingPhone.ownerId}`, {
        country_code: digits ? editingPhone.country_code : "",
        phone: digits || "",
      });
      toast.success(digits ? "Phone updated" : "Phone cleared");
      setEditingPhone(null);
      await loadOwners();
    } catch (e) {
      notifyApiError(toast, e);
    } finally {
      setBusy(false);
    }
  };

  const removeOwner = async (o) => {
    if (!window.confirm(`Remove ${o.email} from platform owners?`)) return;
    setBusy(true);
    try {
      await api.delete(`/platform/owners/${o.id}`);
      toast.success(`${o.email} removed`);
      await loadOwners();
    } catch (e) {
      notifyApiError(toast, e);
    } finally {
      setBusy(false);
    }
  };

  const submitChangePin = async () => {
    if (!/^\d{5}$/.test(currentPin) || !/^\d{5}$/.test(newPin)) {
      toast.error("PINs must be 5 digits");
      return;
    }
    if (newPin !== confirmNewPin) {
      toast.error("New PINs don't match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/platform/owners/change-pin", {
        current_pin: currentPin,
        new_pin: newPin,
      });
      toast.success("PIN updated");
      setShowChangePin(false);
      setCurrentPin("");
      setNewPin("");
      setConfirmNewPin("");
    } catch (e) {
      notifyApiError(toast, e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="bg-[#0e1714] border border-amber-400/30 text-white max-w-lg"
        data-testid="platform-owners-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-black flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-300" />
            Platform Owners
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Manage who can sign in as a platform admin
          </DialogDescription>
        </DialogHeader>

        {step === "pin" && (
          <div className="space-y-3">
            <div className="text-xs text-gray-400 flex items-center gap-1.5">
              <Lock className="w-3.5 h-3.5 text-amber-300" />
              Enter the 5-digit PIN to continue
            </div>
            <Input
              data-testid="pin-input"
              autoFocus
              type="password"
              inputMode="numeric"
              pattern="\d{5}"
              maxLength={5}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 5))}
              placeholder="•••••"
              className="bg-black/40 border-white/10 text-white text-center text-2xl tracking-[12px] font-black"
            />
            <button
              type="button"
              onClick={() => setStep("forgot")}
              data-testid="forgot-pin-link"
              className="text-[11px] uppercase tracking-widest text-amber-300 hover:text-amber-200 font-bold"
            >
              Forgot PIN?
            </button>
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={onClose} className="text-gray-400">
                Cancel
              </Button>
              <Button
                onClick={submitPin}
                disabled={busy || pin.length !== 5}
                data-testid="submit-pin-btn"
                className="bg-amber-500 hover:bg-amber-600 text-black font-black uppercase tracking-widest"
              >
                Unlock
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "forgot" && (
          <div className="space-y-3">
            <div className="text-xs text-gray-400 flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5 text-amber-300" />
              Enter your platform-owner email to receive a recovery code
            </div>
            <Input
              data-testid="forgot-email-input"
              type="email"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              placeholder="you@platform.com"
              className="bg-black/40 border-white/10 text-white"
            />
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setStep("pin")} className="text-gray-400">
                Back
              </Button>
              <Button
                onClick={sendForgot}
                disabled={busy || !forgotEmail.includes("@")}
                data-testid="send-forgot-btn"
                className="bg-amber-500 hover:bg-amber-600 text-black font-black uppercase tracking-widest"
              >
                Send Code
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "reset" && (
          <div className="space-y-3">
            <div className="text-xs text-gray-400 flex items-center gap-1.5">
              <KeyRound className="w-3.5 h-3.5 text-amber-300" />
              Enter the 6-digit recovery code + your new 5-digit PIN
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-widest text-gray-400">
                Recovery code (6 digits)
              </Label>
              <Input
                data-testid="recovery-code-input"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                value={recoveryCode}
                onChange={(e) =>
                  setRecoveryCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                placeholder="000000"
                className="bg-black/40 border-white/10 text-white text-center text-xl tracking-[8px] font-bold"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-400">
                  New PIN
                </Label>
                <Input
                  data-testid="new-pin-input"
                  type="password"
                  inputMode="numeric"
                  pattern="\d{5}"
                  maxLength={5}
                  value={newPin}
                  onChange={(e) =>
                    setNewPin(e.target.value.replace(/\D/g, "").slice(0, 5))
                  }
                  className="bg-black/40 border-white/10 text-white text-center text-lg tracking-[8px] font-bold"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-400">
                  Confirm
                </Label>
                <Input
                  data-testid="confirm-new-pin-input"
                  type="password"
                  inputMode="numeric"
                  pattern="\d{5}"
                  maxLength={5}
                  value={confirmNewPin}
                  onChange={(e) =>
                    setConfirmNewPin(e.target.value.replace(/\D/g, "").slice(0, 5))
                  }
                  className="bg-black/40 border-white/10 text-white text-center text-lg tracking-[8px] font-bold"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setStep("pin")} className="text-gray-400">
                Back
              </Button>
              <Button
                onClick={submitReset}
                disabled={busy}
                data-testid="submit-reset-btn"
                className="bg-amber-500 hover:bg-amber-600 text-black font-black uppercase tracking-widest"
              >
                Reset PIN
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "manage" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs uppercase tracking-widest text-gray-400">
                Active Platform Owners ({owners.length})
              </h3>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowChangePin((s) => !s)}
                  data-testid="toggle-change-pin-btn"
                  className="text-[10px] uppercase tracking-widest font-bold border-amber-400/30 text-amber-200 hover:bg-amber-500/10"
                >
                  <KeyRound className="w-3 h-3 mr-1" /> Change PIN
                </Button>
                <Button
                  size="sm"
                  onClick={() => setShowAdd((s) => !s)}
                  data-testid="toggle-add-owner-btn"
                  className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-widest text-[10px]"
                >
                  <UserPlus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
            </div>

            {showChangePin && (
              <div
                data-testid="change-pin-panel"
                className="border border-amber-400/30 bg-amber-500/5 rounded-lg p-3 space-y-2"
              >
                <div className="text-[10px] uppercase tracking-widest text-amber-200 font-bold">
                  Change PIN
                </div>
                <Input
                  data-testid="current-pin-input"
                  type="password"
                  inputMode="numeric"
                  maxLength={5}
                  placeholder="Current PIN"
                  value={currentPin}
                  onChange={(e) =>
                    setCurrentPin(e.target.value.replace(/\D/g, "").slice(0, 5))
                  }
                  className="bg-black/40 border-white/10 text-white text-center tracking-[8px] font-bold"
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="password"
                    inputMode="numeric"
                    maxLength={5}
                    placeholder="New PIN"
                    value={newPin}
                    onChange={(e) =>
                      setNewPin(e.target.value.replace(/\D/g, "").slice(0, 5))
                    }
                    className="bg-black/40 border-white/10 text-white text-center tracking-[8px] font-bold"
                  />
                  <Input
                    type="password"
                    inputMode="numeric"
                    maxLength={5}
                    placeholder="Confirm"
                    value={confirmNewPin}
                    onChange={(e) =>
                      setConfirmNewPin(e.target.value.replace(/\D/g, "").slice(0, 5))
                    }
                    className="bg-black/40 border-white/10 text-white text-center tracking-[8px] font-bold"
                  />
                </div>
                <Button
                  size="sm"
                  onClick={submitChangePin}
                  disabled={busy}
                  data-testid="save-change-pin-btn"
                  className="w-full bg-amber-500 hover:bg-amber-600 text-black font-black uppercase tracking-widest text-[10px]"
                >
                  Save PIN
                </Button>
              </div>
            )}

            {showAdd && (
              <div
                data-testid="add-owner-panel"
                className="border border-emerald-400/30 bg-emerald-500/5 rounded-lg p-3 space-y-2"
              >
                <div className="text-[10px] uppercase tracking-widest text-emerald-200 font-bold">
                  Add Platform Owner
                </div>

                {/* Method toggle — email-and-password owner OR phone-OTP-only owner. */}
                <div
                  className="grid grid-cols-2 gap-2 p-1 rounded-md bg-black/40 border border-white/10"
                  data-testid="add-owner-method-tabs"
                >
                  <button
                    type="button"
                    onClick={() => setNewOwner((s) => ({ ...s, method: "email" }))}
                    data-testid="add-owner-method-email"
                    className={
                      "flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-sm text-[10px] font-bold uppercase tracking-wider transition-colors " +
                      (newOwner.method === "email"
                        ? "bg-amber-500 text-black"
                        : "text-gray-400 hover:text-white")
                    }
                  >
                    <Mail className="w-3 h-3" /> Email + Password
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewOwner((s) => ({ ...s, method: "phone" }))}
                    data-testid="add-owner-method-phone"
                    className={
                      "flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-sm text-[10px] font-bold uppercase tracking-wider transition-colors " +
                      (newOwner.method === "phone"
                        ? "bg-amber-500 text-black"
                        : "text-gray-400 hover:text-white")
                    }
                  >
                    <Phone className="w-3 h-3" /> Phone OTP only
                  </button>
                </div>

                <Input
                  data-testid="add-owner-name"
                  placeholder="Name (optional)"
                  value={newOwner.name}
                  onChange={(e) =>
                    setNewOwner((s) => ({ ...s, name: e.target.value }))
                  }
                  className="bg-black/40 border-white/10 text-white"
                />

                {newOwner.method === "email" ? (
                  <>
                    <Input
                      data-testid="add-owner-email"
                      placeholder="Email"
                      type="email"
                      value={newOwner.email}
                      onChange={(e) =>
                        setNewOwner((s) => ({ ...s, email: e.target.value }))
                      }
                      className="bg-black/40 border-white/10 text-white"
                    />
                    <Input
                      data-testid="add-owner-password"
                      placeholder="Password"
                      type="password"
                      value={newOwner.password}
                      onChange={(e) =>
                        setNewOwner((s) => ({ ...s, password: e.target.value }))
                      }
                      className="bg-black/40 border-white/10 text-white"
                    />
                    <div className="flex gap-2">
                      <select
                        data-testid="add-owner-country"
                        value={newOwner.country_code}
                        onChange={(e) =>
                          setNewOwner((s) => ({ ...s, country_code: e.target.value }))
                        }
                        className="bg-black/40 border border-white/10 text-white rounded-md px-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/60"
                      >
                        {["+91", "+1", "+44", "+971", "+966", "+65", "+61"].map((c) => (
                          <option key={c} value={c} className="bg-[#121a16]">{c}</option>
                        ))}
                      </select>
                      <div className="relative flex-1">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <Input
                          data-testid="add-owner-phone"
                          placeholder="Phone (optional)"
                          inputMode="numeric"
                          value={newOwner.phone}
                          onChange={(e) =>
                            setNewOwner((s) => ({ ...s, phone: e.target.value }))
                          }
                          className="pl-10 bg-black/40 border-white/10 text-white"
                        />
                      </div>
                    </div>
                    <p className="text-[10px] text-gray-500 tracking-wide">
                      Email + password is the primary login. Phone (optional)
                      also enables SMS OTP sign-in.
                    </p>
                  </>
                ) : (
                  <>
                    <div className="flex gap-2">
                      <select
                        data-testid="add-owner-country"
                        value={newOwner.country_code}
                        onChange={(e) =>
                          setNewOwner((s) => ({ ...s, country_code: e.target.value }))
                        }
                        className="bg-black/40 border border-white/10 text-white rounded-md px-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/60"
                      >
                        {["+91", "+1", "+44", "+971", "+966", "+65", "+61"].map((c) => (
                          <option key={c} value={c} className="bg-[#121a16]">{c}</option>
                        ))}
                      </select>
                      <div className="relative flex-1">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <Input
                          data-testid="add-owner-phone"
                          placeholder="Phone (required)"
                          inputMode="numeric"
                          value={newOwner.phone}
                          onChange={(e) =>
                            setNewOwner((s) => ({ ...s, phone: e.target.value }))
                          }
                          className="pl-10 bg-black/40 border-white/10 text-white"
                        />
                      </div>
                    </div>
                    <p className="text-[10px] text-amber-300/80 tracking-wide">
                      This owner signs in via SMS OTP only — no email or
                      password needed.
                    </p>
                  </>
                )}

                <Button
                  size="sm"
                  onClick={addOwner}
                  disabled={busy}
                  data-testid="save-add-owner-btn"
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-black font-black uppercase tracking-widest text-[10px]"
                >
                  Add Owner
                </Button>
              </div>
            )}

            <div
              data-testid="owners-list"
              className="rounded-lg border border-white/10 max-h-72 overflow-y-auto"
            >
              {/* Column-view toggle — sticky header so the choice stays visible
                  while the admin scrolls through a long list. */}
              <div
                className="sticky top-0 z-10 grid grid-cols-2 gap-1 p-1 bg-[#0e1714] border-b border-white/5"
                data-testid="owners-view-tabs"
              >
                <button
                  type="button"
                  onClick={() => setViewBy("email")}
                  data-testid="owners-view-email"
                  className={
                    "flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-[10px] font-bold uppercase tracking-wider transition-colors " +
                    (viewBy === "email"
                      ? "bg-amber-500 text-black"
                      : "text-gray-400 hover:text-white")
                  }
                >
                  <Mail className="w-3 h-3" /> Email
                </button>
                <button
                  type="button"
                  onClick={() => setViewBy("phone")}
                  data-testid="owners-view-phone"
                  className={
                    "flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-[10px] font-bold uppercase tracking-wider transition-colors " +
                    (viewBy === "phone"
                      ? "bg-amber-500 text-black"
                      : "text-gray-400 hover:text-white")
                  }
                >
                  <Phone className="w-3 h-3" /> Phone
                </button>
              </div>

              <div className="divide-y divide-white/5">
              {owners.map((o) => {
                const isEditing = editingPhone?.ownerId === o.id;
                const phoneStr =
                  o.phone_e164 ||
                  (o.country_code && o.phone ? `${o.country_code}${o.phone}` : "");
                return (
                  <div
                    key={o.id}
                    data-testid={`owner-row-${o.id}`}
                    className="px-3 py-2.5 hover:bg-white/[0.02]"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-bold text-white truncate">
                          {o.name || "—"}
                        </div>
                        {viewBy === "email" ? (
                          !o.phone_only ? (
                            <div className="text-[12px] text-gray-300 font-mono truncate flex items-center gap-1.5 mt-0.5">
                              <Mail className="w-3 h-3 text-amber-300" />
                              {o.email}
                            </div>
                          ) : (
                            <div className="text-[11px] text-gray-500 italic mt-0.5">
                              No email — phone-only owner
                            </div>
                          )
                        ) : (
                          <div className="text-[12px] text-amber-300/90 font-mono truncate flex items-center gap-1.5 mt-0.5">
                            <Phone className="w-3 h-3 text-amber-300" />
                            {phoneStr || (
                              <span className="text-gray-500 italic">no phone</span>
                            )}
                          </div>
                        )}
                        {o.phone_only && viewBy === "phone" && (
                          <div className="text-[9px] uppercase tracking-widest text-amber-300/70 mt-0.5">
                            Phone OTP only
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {o.protected ? (
                          <span
                            data-testid={`owner-protected-${o.id}`}
                            className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-md px-1.5 py-0.5"
                            title="This account is protected and cannot be removed"
                          >
                            <ShieldCheck className="w-2.5 h-2.5" /> Protected
                          </span>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                setEditingPhone(
                                  isEditing
                                    ? null
                                    : {
                                        ownerId: o.id,
                                        country_code: o.country_code || "+91",
                                        phone: o.phone || "",
                                      },
                                )
                              }
                              data-testid={`edit-owner-phone-${o.id}`}
                              className="text-amber-300 hover:bg-amber-500/10 h-7 px-2"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeOwner(o)}
                              data-testid={`remove-owner-${o.id}`}
                              className="text-rose-300 hover:bg-rose-500/10 h-7 px-2"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>

                    {isEditing && (
                      <div
                        data-testid={`edit-phone-panel-${o.id}`}
                        className="mt-2 flex items-center gap-2"
                      >
                        <select
                          value={editingPhone.country_code}
                          onChange={(e) =>
                            setEditingPhone((s) => ({ ...s, country_code: e.target.value }))
                          }
                          className="bg-black/40 border border-white/10 text-white rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-amber-500/60"
                        >
                          {["+91", "+1", "+44", "+971", "+966", "+65", "+61"].map((c) => (
                            <option key={c} value={c} className="bg-[#121a16]">{c}</option>
                          ))}
                        </select>
                        <Input
                          value={editingPhone.phone}
                          onChange={(e) =>
                            setEditingPhone((s) => ({ ...s, phone: e.target.value }))
                          }
                          placeholder="Phone (blank = clear)"
                          inputMode="numeric"
                          data-testid={`edit-phone-input-${o.id}`}
                          className="flex-1 bg-black/40 border-white/10 text-white h-8 text-xs"
                        />
                        <Button
                          size="sm"
                          onClick={saveOwnerPhone}
                          disabled={busy}
                          data-testid={`save-phone-btn-${o.id}`}
                          className="bg-emerald-500 hover:bg-emerald-600 text-black h-8 px-2"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingPhone(null)}
                          className="text-gray-400 h-8 px-2"
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
              {owners.length === 0 && (
                <div className="text-center text-gray-500 text-sm py-4">
                  No platform owners
                </div>
              )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
